import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import SpotlightCard from './SpotlightCard';

const QuickActionButton = ({ 
  isVisible = true,
  onQuickStart,
  className = "",
  id=""
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const containerRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Show on dashboard and library pages only
  const shouldShow = isVisible && (
    location?.pathname === '/' || 
    location?.pathname === '/dashboard-home' || 
    location?.pathname === '/practice-library-resources'
  );

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsExpanded(false);
        setIsMobileMenuOpen(false);
      }
    };

    if (isExpanded || isMobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isExpanded, isMobileMenuOpen]);

  if (!shouldShow) return null;

  const quickActions = [
    {
      label: 'Quick Practice',
      icon: 'Play',
      action: () => {
        navigate('/interview-setup-configuration');
        setIsExpanded(false);
        setIsMobileMenuOpen(false);
      },
      color: 'from-purple-500 to-indigo-500',
      spotlight: 'rgba(139, 92, 246, 0.2)',
      description: 'Start a quick interview session'
    },
    {
      label: 'Mock Interview',
      icon: 'Video',
      action: () => {
        navigate('/interview-setup-configuration?type=mock');
        setIsExpanded(false);
        setIsMobileMenuOpen(false);
      },
      color: 'from-indigo-500 to-purple-500',
      spotlight: 'rgba(99, 102, 241, 0.2)',
      description: 'Full mock interview simulation'
    },
    {
      label: 'Practice Library',
      icon: 'BookOpen',
      action: () => {
        navigate('/practice-library-resources');
        setIsExpanded(false);
        setIsMobileMenuOpen(false);
      },
      color: 'from-green-500 to-teal-500',
      spotlight: 'rgba(16, 185, 129, 0.2)',
      description: 'Browse practice resources'
    }
  ];

  const handleMainAction = () => {
    if (onQuickStart) {
      onQuickStart();
    } else {
      navigate('/interview-setup-configuration');
    }
  };

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <>
      {/* Desktop Version */}
      <div 
        id={id}
        ref={containerRef}
        className={`fixed bottom-6 right-6 z-50 hidden md:block ${className}`}
      >
        {/* Expanded Actions */}
        {isExpanded && (
          <div className="absolute bottom-20 right-0 space-y-3 pb-2">
            {quickActions?.map((action, index) => (
              <div
                key={action?.label}
                className="flex items-center justify-end space-x-3"
                style={{ 
                  animation: `fadeInUp 0.3s ease-out ${index * 80}ms both`,
                  transformOrigin: 'center right'
                }}
              >
                <SpotlightCard 
                  className="custom-spotlight-card" 
                  spotlightColor={action.spotlight}
                >
                  <span className="text-sm font-medium text-white bg-[rgba(30,30,46,0.95)] px-4 py-2 rounded-lg border border-white/10 whitespace-nowrap backdrop-blur-sm shadow-lg">
                    {action?.label}
                  </span>
                </SpotlightCard>
                <button
                  onClick={action?.action}
                  className={`
                    flex items-center justify-center w-12 h-12 rounded-full
                    bg-black/60 text-white
                    shadow-lg hover:shadow-xl transform hover:scale-110 active:scale-95
                    transition-all duration-200 ease-smooth
                  `}
                  style={{
                    boxShadow: `0 4px 20px ${action.spotlight}`
                  }}
                >
                  <Icon name={action?.icon} size={20} />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Main Action Button */}
        <button
          onClick={toggleExpanded}
          className={`
            flex items-center justify-center w-14 h-14 rounded-full
            bg-gradient-to-r from-purple-500 to-indigo-500 text-white
            shadow-lg hover:shadow-2xl transform hover:scale-110 active:scale-95
            transition-all duration-300 ease-smooth button-glow
            ${isExpanded ? 'rotate-45' : 'rotate-0'}
          `}
        >
          <Icon name={isExpanded ? "X" : "Plus"} size={24} strokeWidth={2.5} />
        </button>
      </div>

      {/* Mobile Version */}
      <div 
        ref={containerRef}
        className={`fixed bottom-20 right-4 z-50 md:hidden ${className}`}
      >
        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="absolute bottom-16 right-0 w-64  rounded-2xl border border-white/10 shadow-2xl overflow-hidden animate-slide-up">
            <div className="p-3 space-y-2">
              {quickActions?.map((action, index) => (
                <button
                  key={action?.label}
                  onClick={action?.action}
                  className={`
                    w-full flex items-center space-x-3 p-3 rounded-xl
                    bg-black/80 bg-opacity-10
                    border border-white/10 hover:border-white/20
                    transform active:scale-95 transition-all duration-200
                    group
                  `}
                  style={{
                    animation: `fadeIn 0.2s ease-out ${index * 50}ms both`
                  }}
                >
                  <div 
                    className={`
                      flex items-center justify-center w-10 h-10 rounded-full
                      bg-gradient-to-r ${action?.color} text-white
                      shadow-md group-active:scale-90 transition-transform
                    `}
                  >
                    <Icon name={action?.icon} size={18} />
                  </div>
                  <div className="flex-1 text-left">
                    <div className="text-sm font-semibold text-white">
                      {action?.label}
                    </div>
                    <div className="text-xs text-gray-400">
                      {action?.description}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Mobile Main Button */}
        {/* <SpotlightCard 
          className="custom-spotlight-card" 
          spotlightColor="rgba(139, 92, 246, 0.3)"
        > */}
          <button
            onClick={toggleMobileMenu}
            className={`
              flex items-center justify-center w-14 h-14 rounded-full
              bg-gradient-to-r from-purple-500 to-indigo-500 text-white
              shadow-lg active:scale-90 transform
              transition-all duration-200 ease-smooth button-glow
              ${isMobileMenuOpen ? 'rotate-45' : 'rotate-0'}
            `}
          >
            <Icon 
              name={isMobileMenuOpen ? "X" : "Plus"} 
              size={24} 
              strokeWidth={2.5} 
            />
          </button>
        {/* </SpotlightCard> */}
      </div>

      {/* Backdrop for expanded state */}
      {(isExpanded || isMobileMenuOpen) && (
        <div 
          className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm"
          style={{ animation: 'fadeIn 0.2s ease-out' }}
        />
      )}

      {/* CSS Animation Keyframes */}
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(10px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes slide-up {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes glow {
          0%, 100% {
            box-shadow: 0 0 20px rgba(139, 92, 246, 0.5), 0 4px 15px rgba(0, 0, 0, 0.3);
          }
          50% {
            box-shadow: 0 0 30px rgba(139, 92, 246, 0.8), 0 0 40px rgba(139, 92, 246, 0.4), 0 4px 20px rgba(0, 0, 0, 0.3);
          }
        }
        
        .button-glow {
          animation: glow 2s ease-in-out infinite;
        }

        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }

        /* Smooth transitions */
        * {
          -webkit-tap-highlight-color: transparent;
        }
      `}</style>
    </>
  );
};

export default QuickActionButton;